import { contactAuthUrl } from './apiBase'
import { decodeJwtPayload } from './experience'
import { markLoadPhase, measureLoadPhase } from './loadPerformance'

export type ContactAuthPhase =
  | { kind: 'loading' }
  | { kind: 'error'; message: string; status?: number }
  | {
      kind: 'ready'
      smartAuth: string
      jwtPayload: Record<string, unknown> | null
      jwtPreview: string
    }

export type ContactAuthResult = Exclude<ContactAuthPhase, { kind: 'loading' }>

function readSmartAuth(data: unknown): string | null {
  if (!isRecord(data)) return null
  const sa = data.smart_auth
  return typeof sa === 'string' ? sa : null
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

const inflightAuth = new Map<string, Promise<ContactAuthResult>>()

async function parseContactAuthResponse(res: Response): Promise<ContactAuthResult> {
  if (!res.ok) {
    const body = await res.text()
    return {
      kind: 'error',
      message: `${res.status} ${body.slice(0, 200)}`,
      status: res.status,
    }
  }
  const data: unknown = await res.json()
  const smartAuth = readSmartAuth(data)
  if (!smartAuth) {
    return { kind: 'error', message: 'Invalid auth response' }
  }
  const jwtPayload = decodeJwtPayload(smartAuth)
  const preview = jwtPayload ? JSON.stringify(jwtPayload, null, 2) : '{}'
  return { kind: 'ready', smartAuth, jwtPayload, jwtPreview: preview }
}

async function fetchContactAuthOnce(token: string): Promise<ContactAuthResult> {
  markLoadPhase('shell-auth-start')
  try {
    const res = await fetch(contactAuthUrl(token), {
      headers: { correlationid: crypto.randomUUID() },
    })
    markLoadPhase('shell-auth-response')
    measureLoadPhase('shell-auth', 'shell-auth-start', 'shell-auth-response')
    const parsed = await parseContactAuthResponse(res)
    markLoadPhase('shell-auth-done')
    return parsed
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e)
    return { kind: 'error', message }
  }
}

export function fetchContactAuth(token: string): Promise<ContactAuthResult> {
  const key = token.trim()
  const existing = inflightAuth.get(key)
  if (existing) return existing
  const promise = fetchContactAuthOnce(key)
  inflightAuth.set(key, promise)
  void promise.finally(() => {
    inflightAuth.delete(key)
  })
  return promise
}
